import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, map, of, switchMap } from 'rxjs';
import {
  GET_AGENT_REGISTRATION,
  GET_AGENT_TYPES,
  LIST_AGENT_REGISTRATIONS,
} from '../graphql/agent-applications.graphql';
import * as ActionsGroup from './agent-applications.actions';
import {
  GetAgentRegistrationResponse,
  ListAgentRegistrationsResponse,
} from '../responses/agent-applications.response';

@Injectable({
  providedIn: 'root',
})
export class AgentApplicationsEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  $listAgentRegistrations = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.ListAgentRegistrationsActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListAgentRegistrationsResponse>({
            query: LIST_AGENT_REGISTRATIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              wfa: request.wfa ?? null,
              pageableParam: request.pageableParam,
              agentTypeUuid: request.agentTypeUuid || null,
              statuses: request.statuses && request.statuses.length ? request.statuses : null,
            },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data;
              return ActionsGroup.ListAgentRegistrationsActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.ListAgentRegistrationsActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  $getAgentRegistration = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.GetAgentRegistrationActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<{ getAgentRegistration: GetAgentRegistrationResponse }>({
            query: GET_AGENT_REGISTRATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { agentUuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getAgentRegistration;
              if (payload?.status === false) {
                return ActionsGroup.GetAgentRegistrationActions.failure({
                  error: payload,
                });
              }
              return ActionsGroup.GetAgentRegistrationActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.GetAgentRegistrationActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  $getAgentTypes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionsGroup.GetAgentTypesActions.request),
      switchMap(() =>
        this.graphQLService
          .query<any>({
            query: GET_AGENT_TYPES,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getAgentTypes?.data ?? [];
              return ActionsGroup.GetAgentTypesActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(ActionsGroup.GetAgentTypesActions.failure({ error })),
            ),
          ),
      ),
    ),
  );
}
